// THE SHELL — the topbar and the sidebar, exactly as docs/mockup/index.html draws them.
//
// Topbar, left to right: ☰ · the brand tick + CRM · the page name · (right) the context select,
// the notification bell, EN|DE|FR, the theme toggle, the operator's initial, ⏻ Log out.
//
// Sidebar: four groups — Overview (Today, Review, Inbox) · Network (People, Organisations,
// Pipeline) · Outreach (Signals, Campaigns) · System (Agents, Admin).
//
// THE NAV COUNTS, and why they are allowed to be silent: a nav item with an `activity` key in
// the registry shows a count polled every 15 s from GET /api/proposals/summary, GET /api/today
// and GET /api/mail/summary. Those routes belong to other agents and may not exist yet, so a
// 404, a 501 "not built yet" — or any failure — leaves the count UNSET and the nav item
// silent. It never renders 0. A zero beside "Review" is a claim that the queue is empty; a
// route that has not been built has made no such claim, and the difference matters
// (ATTEST: `unknown` is not `nil`).
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useAuth } from 'auth-sso-kit/client/index.js';
import { NotificationBell } from 'notification-kit/client/index.js';

import { LangSwitch, useT } from '../i18n/index.js';
import { request } from '../api/client.js';
import { useTheme } from './ThemeContext.js';
import { NAV_GROUPS, PAGES, navOwnerOf, type PageId } from '../pages/registry.js';
import './AppShell.de.js';
import './AppShell.fr.js';

const ACTIVITY_POLL_MS = 15_000;

// The phone/tablet line (attest.css PHONE section): below it the sidebar goes off-canvas and
// opens as a drawer instead of the desktop collapse-to-48px toggle.
const PHONE_QUERY = '(max-width: 820px)';

/** Tracks the phone breakpoint live (resize, rotation), not just at first paint. */
function usePhoneBreakpoint(): boolean {
  const [isPhone, setIsPhone] = useState(() => {
    try { return typeof window !== 'undefined' && window.matchMedia(PHONE_QUERY).matches; } catch { return false; }
  });
  useEffect(() => {
    let mq: MediaQueryList;
    try { mq = window.matchMedia(PHONE_QUERY); } catch { return; }
    const onChange = () => setIsPhone(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isPhone;
}

/** What a nav item may show. `undefined` means "nobody counted" and renders nothing. */
type Counts = Partial<Record<NonNullable<(typeof PAGES)[number]['activity']>, number>>;

export default function AppShell({ currentPage, children }: { currentPage: PageId; children: ReactNode }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const t = useT();

  const [collapsed, setCollapsed] = useState(false);
  const isPhone = usePhoneBreakpoint();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [counts, setCounts] = useState<Counts>({});

  // Growing back past the breakpoint (rotate, resize, an external monitor) always leaves the
  // drawer closed rather than stuck open behind a now-static sidebar.
  useEffect(() => { if (!isPhone) setDrawerOpen(false); }, [isPhone]);

  // While the drawer is open: Escape closes it, and the body cannot scroll behind it.
  useEffect(() => {
    if (!isPhone || !drawerOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrawerOpen(false); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [isPhone, drawerOpen]);

  const toggleSidebar = () => { if (isPhone) setDrawerOpen((v) => !v); else setCollapsed((v) => !v); };
  const closeDrawer = () => setDrawerOpen(false);

  // Each summary is fetched on its own and merged on its own: one route being absent must not
  // blank the other's count. A rejection leaves that key untouched — the last good number
  // stays, exactly as usePoll's law says.
  const loadCounts = useCallback(async () => {
    const [proposals, today, mail] = await Promise.allSettled([
      request<{ unheld?: number }>('/proposals/summary'),
      request<{ signals?: number | null }>('/today'),
      // Round 2. The mail agent owns /api/mail/summary; until it lands the route answers 501,
      // which lands here as a rejection and leaves the count UNSET — the same silence a 404
      // buys. A 501 is a route that exists and is not built; either way nothing was counted,
      // and the nav must not say zero.
      request<{ needs_me?: number | null }>('/mail/summary'),
    ]);
    setCounts((prev) => {
      const next = { ...prev };
      if (proposals.status === 'fulfilled' && typeof proposals.value?.unheld === 'number') {
        next.proposals = proposals.value.unheld;
      }
      // `signals: null` is the documented "no producer yet" answer — honour it as unknown,
      // which means the nav says nothing at all.
      if (today.status === 'fulfilled' && typeof today.value?.signals === 'number') {
        next['today.signals'] = today.value.signals;
      }
      if (mail.status === 'fulfilled' && typeof mail.value?.needs_me === 'number') {
        next['inbox.needs_me'] = mail.value.needs_me;
      }
      return next;
    });
  }, []);

  useEffect(() => {
    void loadCounts();
    const timer = setInterval(() => { void loadCounts(); }, ACTIVITY_POLL_MS);
    return () => clearInterval(timer);
  }, [loadCounts, currentPage]);

  const visiblePages = PAGES.filter((p) => !p.hidden && (!p.adminOnly || user?.isAdmin));
  const litNav = navOwnerOf(currentPage);
  const pageTitle = PAGES.find((p) => p.id === currentPage)?.title ?? '';
  const initial = (user?.name || user?.email || '?')[0].toUpperCase();

  return (
    <div className={`shell${collapsed && !isPhone ? ' shell--collapsed' : ''}${isPhone && drawerOpen ? ' shell--drawer-open' : ''}`}>
      <header className="topbar">
        <div className="topbar-left">
          <button
            className="btn btn-icon sidebar-toggle"
            onClick={toggleSidebar}
            aria-expanded={isPhone ? drawerOpen : undefined}
            aria-label={isPhone ? (drawerOpen ? t('Close menu') : t('Open menu')) : t('Toggle sidebar')}
            title={isPhone ? (drawerOpen ? t('Close menu') : t('Open menu')) : t('Toggle sidebar')}
          >☰</button>
          <div className="brand">
            <span className="brand-mark" />
            <span className="brand-name">CRM</span>
          </div>
          <span className="brand-sep" />
          <span className="brand-page">{t(pageTitle)}</span>
        </div>
        <div className="topbar-right">
          {/* The context select. Its options come from the organisations route, which the
              organisations agent owns; until then it offers the one honest option it has. */}
          <select className="form-select topbar-eng-select" defaultValue="" title={t('Context')} disabled>
            <option value="">{t('— every organisation —')}</option>
          </select>
          <NotificationBell onNavigate={(hash: string) => { window.location.hash = hash; }} />
          <LangSwitch />
          <button className="theme-btn" onClick={toggle} title={t('Toggle theme')}>{theme === 'dark' ? '◐' : '◑'}</button>
          <span className="user-tag" title={user?.email ?? ''}><span className="user-initial">{initial}</span></span>
          <button className="btn btn-ghost btn-sm logout-btn" onClick={() => void logout()} aria-label={t('Log out')} title={t('Log out')}>
            <span className="logout-icon" aria-hidden="true">⏻</span>
            <span className="logout-label">{t('Log out')}</span>
          </button>
        </div>
      </header>

      {isPhone && drawerOpen && <div className="shell-scrim" aria-hidden="true" onClick={closeDrawer} />}

      <aside className="sidebar">
        {NAV_GROUPS.map((group, i) => {
          const pages = visiblePages.filter((p) => p.navGroup === group);
          if (!pages.length) return null;
          return (
            <div key={group}>
              {i > 0 && <div className="sidebar-rule" />}
              <div className="sidebar-block">
                <div className="sidebar-label">{t(group)}</div>
                {pages.map((p) => {
                  const count = p.activity ? counts[p.activity] : undefined;
                  return (
                    <a
                      key={p.id}
                      href={`#${p.id}`}
                      className={`nav-link${litNav === p.id ? ' active' : ''}`}
                      onClick={closeDrawer}
                    >
                      {t(p.title)}
                      {/* Rendered only when something actually counted. Never a zero from a
                          route that does not exist. */}
                      {count !== undefined && count > 0 && (
                        <span className="nav-activity">
                          <span className="nav-activity-count">{count > 99 ? '99+' : count}</span>
                        </span>
                      )}
                    </a>
                  );
                })}
              </div>
            </div>
          );
        })}
      </aside>

      <main className="content">{children}</main>
    </div>
  );
}
